import { memo, useCallback } from 'react';
import xor from 'lodash/xor';
import Loader from '@picsart/loader/Loader';

import ConfirmModal from 'components/common/Modals/ConfirmModal';
import { confirmModalTypes } from 'components/common/Modals/ConfirmModal/types';
import { DataActionTypes } from 'pages/Batch/sections/Context';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import errorLogger from 'services/errorLogger';
import { BATCH_TOOLS } from 'pages/Batch/configs';
import analytics from 'pages/Batch/utils/analytics';
import EmptyMain from 'pages/Batch/components/EmptyMain';
import DropImage from 'pages/Batch/components/DropImage';

import BatchDataService from '../Context/BatchDataService';

import GridContent from './GridContent';
import Toolbar from './Toolbar';
import useStyles from './styles';

interface IMainContent {
  showDragContent: boolean;
}

const MainContent = ({ showDragContent }: IMainContent) => {
  const {
    data,
    isLoading,
    activeTool,
    selectedData,
    selectedKeys,
    setSelectedKeys,
    dispatchData,
    isWeb,
    showDeleteItemConfirmModal,
    setShowDeleteItemConfirmModal,
  } = useBatchData();
  const classes = useStyles({ isWeb });

  const selectAll = useCallback(() => {
    setSelectedKeys(prev => {
      analytics.clickEvent(prev.length ? 'select_all_canceled' : 'select_all');
      return prev.length === data.length ? [] : data.map(el => el.key);
    });
    errorLogger.leaveBreadcrumb('toolbar_click', { button: 'select_all' });
  }, [data, setSelectedKeys]);

  const selectImages = useCallback((key: string) => {
    if (!activeTool) {
      setSelectedKeys(prev => {
        analytics.clickEvent(prev.includes(key) ? 'item_selection_canceled' : 'item_selection');
        return xor(prev, [key]);
      });
    }
  }, [setSelectedKeys, activeTool]);

  const handleDeleteImages = useCallback((type: confirmModalTypes) => {
    if (type === confirmModalTypes.secondChoiceAction) {
      dispatchData({
        type: DataActionTypes.DELETE_DATA,
        keys: selectedKeys,
      });
      setSelectedKeys([]);
      isWeb && selectedKeys.forEach(key => BatchDataService.removeData(key));
      analytics.clickEvent(selectedKeys.length > 1 ? 'delete' : 'delete_single_item');

      errorLogger.leaveBreadcrumb('toolbar_click', { button: 'delete' });
    }
    setShowDeleteItemConfirmModal(false);
  }, [dispatchData, selectedKeys, setSelectedKeys, setShowDeleteItemConfirmModal, isWeb]);

  const handleDeleteImage = useCallback((key: string) => {
    setSelectedKeys([key]);
    setShowDeleteItemConfirmModal(true);
  }, [setSelectedKeys, setShowDeleteItemConfirmModal]);

  return (
    <>
      <ConfirmModal
        showCloseIcon
        show={showDeleteItemConfirmModal}
        onAction={handleDeleteImages}
        title={`Delete selected item${selectedKeys.length > 1 ? 's' : ''}`}
        description={`${selectedKeys.length} item${selectedKeys.length > 1 ? 's' : ''} will be deleted permanently.`}
        secondChoice="Delete"
        firstChoice="Cancel"
      />

      {isLoading && <Loader isWithoutBackground />}

      {data.length ? (
        <div className={classes.mainContent}>
          {showDragContent && <DropImage />}

          {(!activeTool || activeTool === BATCH_TOOLS.upload) && !!data.length && isWeb && (
            <Toolbar
              onDelete={() => setShowDeleteItemConfirmModal(true)}
              onSelectAll={selectAll}
              isAllSelected={selectedKeys.length === data.length}
            />
          )}

          <GridContent
            onSelectImages={selectImages}
            onDeleteImage={handleDeleteImage}
            data={!activeTool || !selectedData.length ? data : selectedData}
          />
        </div>
      ) : (
        <EmptyMain />
      )}
    </>
  );
};

export default memo(MainContent);
